import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Card,
  Typography,
  Stack,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  useMediaQuery,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useTheme } from "@mui/material";
import { tokens } from "../theme";
import { apiUrl } from "../services/api";
import {
  AttachMoney,
  CarRental,
  DirectionsCar,
  MoneyOff,
} from "@mui/icons-material";
import Header from "../components/Header";
import { format } from "date-fns";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [alertData, setAlertData] = useState([]);
  const [etatData, setEtatData] = useState([]);
  const [voituresDisponibles, setVoituresDisponibles] = useState([]);
  const [voituresLoues, setVoituresLoues] = useState([]);
  // const [yearlyProfitData, setYearlyProfitData] = useState([]);
  const [monthlyProfitData, setMonthlyProfitData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [expiringDocuments, setExpiringDocuments] = useState([]);
  const [charges, setCharges] = useState(0);
  const [profits, setProfits] = useState(0);
  const [returnedVoitures, setReturnedVoitures] = useState([]);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return format(date, "yyyy-MM-dd");
  };

  const fetchVoituresLoues = async () => {
    try {
      const [voituresResponse, contratsResponse] = await Promise.all([
        axios.get(`${apiUrl}/voiture`),
        axios.get(`${apiUrl}/contrat`),
      ]);

      const voitures = voituresResponse.data;
      const contrats = contratsResponse.data;

      const today = new Date();

      const filteredVoituresLoues = voitures.filter((voiture) => {
        const voitureLoues = contrats.filter(
          (contrat) => contrat.id_voiture === voiture.id_voiture
        );

        if (voitureLoues.length !== 0) {
          return voitureLoues.some((contrat) => {
            const retourDate = new Date(contrat.date_retour);
            return retourDate > today;
          });
        }
      });
      setVoituresLoues(filteredVoituresLoues);
    } catch (err) {
      console.error("Error fetching voitures loues:", err);
    }
  };

  const fetchAlert = async () => {
    try {
      const response = await axios.get(`${apiUrl}/alert/daily/alert`);
      setAlertData(response.data);
    } catch (err) {
      console.error("Error fetching alerts:", err);
    }
  };

  const fetchEtat = async () => {
    try {
      const response = await axios.get(`${apiUrl}/etat/daily/etat`);
      setEtatData(response.data);
    } catch (err) {
      console.error("Error fetching etats:", err);
    }
  };

  const fetchVoituresDisponible = async () => {
    try {
      const response = await axios.get(`${apiUrl}/voiture/disponible/voiture`);
      setVoituresDisponibles(response.data);
    } catch (err) {
      console.error("Error fetching voitures disponibles:", err);
    }
  };

  const fetchCharges = async () => {
    try {
      const response = await axios.get(`${apiUrl}/charge/daily/charge`);
      const daily_chatges = response.data.map((item) => item.daily_charges);
      daily_chatges > 0 ? setCharges(daily_chatges) : setCharges(0);
    } catch (err) {
      console.error("Error fetching daily charges:", err);
    }
  };

  const fetchReturnedVoitures = async () => {
    try {
      const response = await axios.get(`${apiUrl}/contrat/returned/voiture`);
      setReturnedVoitures(response.data);
    } catch (err) {
      console.error("Error fetching returned voitures:", err);
    }
  };

  const fetchProfits = async () => {
    try {
      const response = await axios.get(`${apiUrl}/paiement/daily/profit`);
      const daily_profit = response.data[0]?.daily_profit || 0;
      setProfits(daily_profit);
    } catch (err) {
      console.error("Error fetching daily profit:", err);
      setProfits(0);
    }
  };
  
  const fetchProfitData = async () => {
    try {
      const [
        yearlyProfitResponse,
        monthlyProfitResponse,
        yearlyChargesResponse,
        monthlyChargesResponse,
      ] = await Promise.all([
        axios.get(`${apiUrl}/paiement/profits/yearly`),
        axios.get(`${apiUrl}/paiement/profits/monthly/${selectedYear}`),
        axios.get(`${apiUrl}/charge/yearly/charges`),
        axios.get(`${apiUrl}/charge/monthly/charges/${selectedYear}`),
      ]);

      // Set available years for dropdown
      const years = yearlyProfitResponse.data.map((item) => item.year);
      setAvailableYears(years.length > 0 ? years : [new Date().getFullYear()]);

      // Create an array with all months, initialized with 0 profit and 0 charges
      const allMonths = Array.from({ length: 12 }, (_, i) => ({
        month: i + 1,
        total_profit: 0,
        total_depense: 0,
      }));

      // Update the profits for months with data
      monthlyProfitResponse.data.forEach((item) => {
        allMonths[item.month - 1].total_profit = item.total_profit;
      });

      // Update the charges for months with data
      monthlyChargesResponse.data.forEach((item) => {
        allMonths[item.month - 1].total_depense = item.total_charges;
      });

      setMonthlyProfitData(allMonths);
    } catch (error) {
      console.error("Error fetching profit data:", error);
      // Set empty data if there's an error
      setMonthlyProfitData([]);
      setAvailableYears([new Date().getFullYear()]);
    }
  };

  const fetchExpiringDocuments = async () => {
    try {
      const response = await axios.get(`${apiUrl}/papier/detailed/voiture`);
      const documents = response.data;
      const today = new Date();
      const thirtyDaysFromNow = new Date(
        today.getTime() + 30 * 24 * 60 * 60 * 1000
      );

      const expiring = documents.reduce((acc, doc) => {
        const expiringDocs = {
          id_voiture: doc.id_voiture,
          marque: doc.marque,
          matricule: doc.matricule,
          documents: [],
        };

        if (new Date(doc.date_assurance) <= thirtyDaysFromNow) {
          expiringDocs.documents.push({
            type: "Assurance",
            date: new Date(doc.date_assurance),
          });
        }
        if (new Date(doc.date_vignette) <= thirtyDaysFromNow) {
          expiringDocs.documents.push({
            type: "Vignette",
            date: new Date(doc.date_vignette),
          });
        }
        if (new Date(doc.date_visit) <= thirtyDaysFromNow) {
          expiringDocs.documents.push({
            type: "Visite technique",
            date: new Date(doc.date_visit),
          });
        }

        if (expiringDocs.documents.length > 0) {
          acc.push(expiringDocs);
        }

        return acc;
      }, []);

      setExpiringDocuments(expiring);
    } catch (error) {
      console.error("Error fetching expiring documents:", error);
    }
  };

  const calculateDaysBetween = (dateRetour) => {
    const returnDate = new Date(dateRetour);

    const today = new Date();
    const timeDifference = today - returnDate;
    const daysDifference = Math.abs(
      Math.ceil(timeDifference / (1000 * 60 * 60 * 24))
    );

    return " reste " + daysDifference + " Jour(s)";
  };

  useEffect(() => {
    fetchVoituresDisponible();
    fetchVoituresLoues();
    fetchProfits();
    fetchCharges();
    fetchExpiringDocuments();
    fetchProfitData();
    fetchAlert();
    fetchEtat();
    fetchReturnedVoitures();
  }, [selectedYear]);

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const monthlyChartData = monthlyProfitData.map((item) => ({
    month: new Date(selectedYear, item.month - 1, 1).toLocaleString("default", {
      month: "short",
    }),
    profit: item.total_profit,
    depense: item.total_depense,
  }));

  return (
    <Box>
      <Header
        title="Tableau de bord"
        subtitle="Toutes les informations sur la gestion des voitures et leurs revenus/dépenses."
      />
      <Box m="20px 0 0 0" maxHeight={isMobile ? "100%" : "75vh"}>
        <Stack
          direction="column"
          spacing={2}
          divider={<Divider orientation="horizontal" flexItem />}
        >
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            sx={{
              justifyContent: "space-evenly",
              alignItems: "center",
            }}
          >
            <Card
              sx={{
                width: isMobile ? "100%" : 700,
                height: isMobile ? "auto" : 130,
                minWidth: isMobile ? "100%" : 430,
                borderRadius: 3,
                backgroundColor: colors.blueAccent[600],
                mb: isMobile ? 2 : 0,
              }}
            >
              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="flex-start"
                alignItems="center"
                p={2}
              >
                <DirectionsCar
                  sx={{ fontSize: isMobile ? 60 : 100, color: "white" }}
                />
                <Box textAlign={isMobile ? "center" : "left"}>
                  <Typography
                    sx={{ color: "white", fontSize: isMobile ? 24 : 35 }}
                  >
                    Voitures Disponibles
                  </Typography>
                  <Typography
                    sx={{ color: "white", fontSize: isMobile ? 18 : 20 }}
                  >
                    {voituresDisponibles.length} Voitures
                  </Typography>
                </Box>
              </Box>
            </Card>
            <Card
              sx={{
                width: isMobile ? "100%" : 700,
                height: isMobile ? "auto" : 130,
                minWidth: isMobile ? "100%" : 350,
                borderRadius: 3,
                backgroundColor: colors.redAccent[600],
              }}
            >
              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="flex-start"
                alignItems="center"
                p={2}
              >
                <CarRental
                  sx={{ fontSize: isMobile ? 60 : 100, color: "white" }}
                />
                <Box textAlign={isMobile ? "center" : "left"}>
                  <Typography
                    sx={{ color: "white", fontSize: isMobile ? 24 : 35 }}
                  >
                    Voitures Loués
                  </Typography>
                  <Typography
                    sx={{ color: "white", fontSize: isMobile ? 18 : 20 }}
                  >
                    {voituresLoues.length} Voitures
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Stack>

          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            sx={{
              justifyContent: "space-evenly",
              alignItems: "center",
            }}
          >
            {/* Profit Card */}
            <Card
              sx={{
                width: isMobile ? "100%" : 300,
                height: isMobile ? "auto" : 70,
                minWidth: isMobile ? "100%" : 400,
                borderRadius: 3,
                backgroundColor: colors.greenAccent[600],
                mb: isMobile ? 2 : 0,
              }}
            >
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={2}
              >
                <Typography
                  sx={{ color: "white", fontSize: isMobile ? 20 : 30 }}
                >
                  Profit:{" "}
                  <span style={{ color: colors.primary }}>{profits}</span>Dh
                </Typography>
              </Box>
            </Card>

            {/* Dépenses Card */}
            <Card
              sx={{
                width: isMobile ? "100%" : 300,
                height: isMobile ? "auto" : 70,
                minWidth: isMobile ? "100%" : 400,
                borderRadius: 3,
                backgroundColor: colors.greenAccent[600],
                mb: isMobile ? 2 : 0,
              }}
            >
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={2}
              >
                <Typography
                  sx={{ color: "white", fontSize: isMobile ? 20 : 30 }}
                >
                  Dépenses:{" "}
                  <span style={{ color: colors.primary }}>{charges}</span>Dh
                </Typography>
              </Box>
            </Card>

            {/* Total Net Card */}
            <Card
              sx={{
                width: isMobile ? "100%" : 400,
                height: isMobile ? "auto" : 70,
                minWidth: isMobile ? "100%" : 400,
                borderRadius: 3,
                backgroundColor: colors.greenAccent[600],
              }}
            >
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={2}
              >
                <Typography
                  sx={{ color: "white", fontSize: isMobile ? 20 : 30 }}
                >
                  Total Net:{" "}
                  <span style={{ color: colors.primary }}>
                    {profits - charges}
                  </span>
                  Dh
                </Typography>
              </Box>
            </Card>
          </Stack>

          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            divider={
              isMobile ? null : <Divider orientation="vertical" flexItem />
            }
            sx={{
              justifyContent: "space-evenly",
              alignItems: "start",
            }}
          >
            <Box width={isMobile ? "100%" : "auto"}>
              <FormControl
                sx={{ m: 1, minWidth: 120, width: isMobile ? "100%" : "auto" }}
              >
                <InputLabel id="year-select-label">Année</InputLabel>
                <Select
                  labelId="year-select-label"
                  id="year-select"
                  value={selectedYear}
                  label="Année"
                  onChange={handleYearChange}
                >
                  {availableYears.length > 0 ? (
                    availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={new Date().getFullYear()} disabled>
                      Pas de données disponibles
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              <Typography variant="h6" align="center" gutterBottom>
                Profits Mensuels pour {selectedYear}
              </Typography>
              <BarChart
                dataset={monthlyChartData}
                xAxis={[{ scaleType: "band", dataKey: "month" }]}
                series={[
                  {
                    dataKey: "profit",
                    label: "Revenu mensuel",
                    color: colors.greenAccent[500],
                  },
                  {
                    dataKey: "depense",
                    label: "Dépenses mensuels",
                    color: colors.redAccent[500],
                  },
                ]}
                height={isMobile ? 250 : 350}
                width={isMobile ? window.innerWidth - 40 : 800}
              />
            </Box>

            <Stack
              direction="column"
              spacing={2}
              sx={{
                width: isMobile ? "100%" : "auto",
                mt: isMobile ? 2 : 0,
              }}
            >
              {/* Voitures Loués */}
              <Box sx={{ width: "100%" }}>
                <Typography variant="h5" gutterBottom>
                  Voitures Loués
                </Typography>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography color={colors.greenAccent[500]}>
                      Voitures Loués ({returnedVoitures.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                      {returnedVoitures.map((car, index) => (
                        <Box
                          key={index}
                          sx={{
                            mb: 2,
                            p: 1,
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            color={colors.greenAccent[500]}
                          >
                            Voiture: {car.marque + " " + car.matricule}
                          </Typography>
                          <Typography>
                            {"Date de retour de la voiture: " +
                              formatDate(car.date_retour) +
                              " " +
                              calculateDaysBetween(formatDate(car.date_retour))}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>

              {/* Alerts */}
              <Box sx={{ width: "100%" }}>
                <Typography variant="h5" gutterBottom>
                  Alerts
                </Typography>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography color={colors.greenAccent[500]}>
                      les alerts important d'aujourd'hui ({alertData.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                      {alertData.map((alert, index) => (
                        <Box
                          key={index}
                          sx={{
                            mb: 2,
                            p: 1,
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            color={colors.greenAccent[500]}
                          >
                            Alert N°: {alert.id_alert}
                          </Typography>
                          <Typography>{alert.description}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>

              {/* Voitures retournées du garage */}
              <Box sx={{ width: "100%" }}>
                <Typography variant="h5" gutterBottom>
                  Voitures retournées du garage
                </Typography>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography color={colors.greenAccent[500]}>
                      les voitures retournées du garage aujourd'hui (
                      {etatData.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                      {etatData.map((etat, index) => (
                        <Box
                          key={index}
                          sx={{
                            mb: 2,
                            p: 1,
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            color={colors.greenAccent[500]}
                          >
                            Voiture: {etat.marque + " " + etat.matricule}
                          </Typography>
                          <Typography>{etat.description}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>

              {/* Documents en expiration */}
              <Box sx={{ width: "100%" }}>
                <Typography variant="h5" gutterBottom>
                  Documents en expiration
                </Typography>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography color={colors.greenAccent[500]}>
                      Voitures avec documents à renouveler prochainement (
                      {expiringDocuments.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                      {expiringDocuments.map((car, index) => (
                        <Box
                          key={index}
                          sx={{
                            mb: 2,
                            p: 1,
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            color={colors.greenAccent[500]}
                          >
                            Voiture: {car.marque + " " + car.matricule}
                          </Typography>
                          {car.documents.map((doc, docIndex) => (
                            <Typography key={docIndex}>
                              {doc.type}: {formatDate(doc.date)}
                            </Typography>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default Dashboard;
